// The person's UI language, as the suite stores it (issue 673).
//
// Every app of a workspace lives on ONE origin in production (`{slug}.{domain}`,
// path-routed per module), so a language picked in one app is a localStorage
// write every other app can read. It only works if they all read and write the
// SAME key. They did not: workspace-ui and time-ui used the suite-wide
// `kb_locale`, while people-ui and backoffice-ui wrote a tenant-scoped
// `kb_locale:<slug>` and read it first — so a language switched in People never
// reached the suite, and one switched in the suite lost to People's stale copy.
//
// The language is the person's, not the workspace's: `kb_locale` is the one key.
// The tenant-scoped key is read only as a fallback, for a browser whose only
// record of the choice is the old scoped one, and is never written again.
//
// A full page load reads the key at boot. A page that is NOT reloaded — a tab
// returned to, a back navigation restored from the browser's page cache, or
// another tab switching the language — needs `installLocaleSync`.

import { safeGetItem, safeSetItem } from './activeRoleStorage';

/** The suite-wide localStorage key holding the chosen UI language. */
export const LOCALE_STORAGE_KEY = 'kb_locale';

function supportedOrNull<T extends string>(value: string | null, supported: readonly T[]): T | null {
  return value && (supported as readonly string[]).includes(value) ? (value as T) : null;
}

/**
 * The stored language if this app supports it, else null: the suite-wide key
 * first, then the legacy tenant-scoped key for `tenantSlug`.
 */
export function readStoredLocale<T extends string>(supported: readonly T[], tenantSlug?: string | null): T | null {
  const shared = supportedOrNull(safeGetItem(LOCALE_STORAGE_KEY), supported);
  if (shared) return shared;
  const slug = String(tenantSlug || '').trim();
  return slug ? supportedOrNull(safeGetItem(`${LOCALE_STORAGE_KEY}:${slug}`), supported) : null;
}

/** Store the person's language for every app on this origin. */
export function writeStoredLocale(value: string): void {
  safeSetItem(LOCALE_STORAGE_KEY, value);
}

export type LocaleSyncOptions = {
  /** The stored language this app would use now (its `readStoredLocale` call), or null. */
  read: () => string | null;
  /** The language the app is showing. */
  current: () => string;
  /** Show `next` — WITHOUT writing it back (it came from storage). */
  apply: (next: string) => void;
};

/**
 * Keep an app's language in step with the stored choice while it stays loaded:
 * re-read on focus, on becoming visible, on a page-cache restore, and on another
 * tab's write to the key. Returns a function that removes the listeners.
 */
export function installLocaleSync(opts: LocaleSyncOptions): () => void {
  if (typeof window === 'undefined') return () => {};
  const sync = () => {
    const next = opts.read();
    if (next && next !== opts.current()) opts.apply(next);
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') sync();
  };
  const onStorage = (event: StorageEvent) => {
    // `key === null` is a storage.clear() in another tab.
    if (event.key === null || event.key === LOCALE_STORAGE_KEY) sync();
  };
  window.addEventListener('focus', sync);
  window.addEventListener('pageshow', sync);
  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    window.removeEventListener('focus', sync);
    window.removeEventListener('pageshow', sync);
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
